import React, { useState, useContext } from 'react'
import classnames from 'classnames'

// App Context
import { AppContext } from '../../../../../../contexts'

// Hooks
import { useAppreciationIndicator } from '../../../../../../hooks/listing'
import { useLogGAEvent } from '../../../../../../hooks/googleAnalytics'

// Helpers
import { roundWithPrecision } from '../../../../../../helpers'
import {
  buildSliderValues,
  hijackSliderValues,
} from '../../../../../../helpers/sliderHelpers'

// Components
import {
  Panel,
  CircleIcon,
  Text,
  Slider,
  PanelDivider,
  If,
} from '../../../../../../components'

// Constants
import { GREEN, ORANGE } from '../../../../../../constants/indicators'
import { LISTING_EVAL_SLIDER_MOVED } from '../../../../../../constants/googleAnalytics'
import {
  FALLBACK_APPRECIATION_PERCENT,
  STEADY_APPRECIATION_PERCENT,
  FAST_APPRECIATION_PERCENT,
} from '../../../../../../constants/appreciation'

// Assets
import { HomebotIcon } from '../../../../../../components/Svg'
import { LightBulb } from '../../../../../../components/Svg'

// Styles
import baseStyles from '../base.module.scss'

const Appreciation = () => {
  const indicator = useAppreciationIndicator()
  const logGA = useLogGAEvent(LISTING_EVAL_SLIDER_MOVED, {
    debounceInterval: 5000,
  })

  const { theme, color, appreciationPercent, yearsOfData } = indicator || {}

  const projectedAppreciation = () => {
    if (appreciationPercent === null || appreciationPercent < 5)
      return FALLBACK_APPRECIATION_PERCENT
    if (appreciationPercent >= 10) return FAST_APPRECIATION_PERCENT
    if (appreciationPercent >= 5) return STEADY_APPRECIATION_PERCENT
  }
  const projectedAppreciationValue = projectedAppreciation()

  const { appreciationSliderPercent, setAppreciationSliderPercent } =
    useContext(AppContext)

  const [sliderValue, setSliderValue] = useState(
    appreciationSliderPercent || projectedAppreciationValue
  )

  if (!indicator) {
    return null
  }

  const headers = {
    [GREEN]: {
      heading:
        appreciationPercent >= 10
          ? 'FAST GROWTH MARKET'
          : 'STEADY GROWTH MARKET',
      icon: appreciationPercent >= 10 ? 'rabbit' : 'dog',
    },
    [ORANGE]: {
      heading: 'SLOW GROWTH MARKET',
      icon: 'turtle',
    },
  }

  const title = headers[color].heading
  const icon = headers[color].icon

  const BotHead = () => {
    return (
      <div className={baseStyles.hbHead}>
        <HomebotIcon />
      </div>
    )
  }

  const markers = [
    {
      value: projectedAppreciationValue,
      direction: 'down',
      content: <BotHead />,
    },
  ]

  const step = 0.25
  const min = -10
  const max = 20

  const sliderValues = buildSliderValues({
    min,
    max,
    botHeadValue: projectedAppreciationValue,
  })

  const onSliderChange = e => {
    const num = e.target.value

    const requestValue = hijackSliderValues({
      markerValues: [projectedAppreciationValue],
      allSpecialValues: sliderValues,
      interval: step,
      sliderValue: num,
    })

    setSliderValue(requestValue)
    setAppreciationSliderPercent(requestValue)

    logGA({
      slider_name: 'Custom Appreciation Percent',
      slider_value: sliderValue,
    })
  }

  return (
    <Panel className={baseStyles.panel}>
      <div className={baseStyles.label}>
        <CircleIcon
          className={baseStyles.icon}
          backgroundColor={theme}
          icon={icon}
        />
        <Text
          letterSpacing='large'
          size='s'
          color={color}
          weight='bold'
          className={classnames(baseStyles.heading, baseStyles.center)}
        >
          {title}
        </Text>
      </div>
      <Text size='m' color='white'>
        How quickly would you bet this home will grow in value?
      </Text>
      <Text
        size='xxxl'
        color='eggShell'
        weight='light'
        className={baseStyles.amount}
        element='span'
      >
        {roundWithPrecision(sliderValue, 2)}
        {'% '}
      </Text>
      <Text
        size='l'
        color='eggShell'
        weight='light'
        element='span'
        className={baseStyles.amount}
      >
        per year
      </Text>
      <Slider
        name={'appreciationSlider'}
        min={min}
        max={max}
        step={step}
        minPhrase={'slow'}
        maxPhrase={'fast'}
        value={sliderValue}
        onChange={onSliderChange}
        markers={markers}
        className={baseStyles.slider}
      />
      <PanelDivider footer icon={LightBulb}>
        <Text size='s'>
          <Text element='span' color='white' weight='semibold'>
            {projectedAppreciationValue}%
          </Text>{' '}
          <If condition={yearsOfData === null || yearsOfData === 0}>
            is our conservative assumption based on the home’s projected growth.
          </If>
          <If condition={(yearsOfData !== null) & (yearsOfData !== 0)}>
            is our conservative projection based on the past {yearsOfData} year
            average of
            <Text element='span' color='white' weight='semibold'>
              {' '}
              {roundWithPrecision(appreciationPercent, 2)}
              {'% growth.'}
            </Text>
          </If>
        </Text>
      </PanelDivider>
    </Panel>
  )
}

export default Appreciation
